import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useCallback, useState } from "react";
import { useGetLlmHealthChecks } from "./api/useGetLlmHealthChecks";
import CenterLoader from "@/shared/components/loader/CenterLoader";
import { LlmHealthVisualization } from "./components/LlmHealthVisualization";
import { RefreshCw } from "lucide-react";

// Auto-refresh configuration
const AUTO_REFRESH_INTERVAL = 60 * 1000; // 60 seconds

// Time range options
const TIME_RANGES = [
  { value: "1h", label: "Last 1 hour", hours: 1 },
  { value: "4h", label: "Last 4 hours", hours: 4 },
  { value: "12h", label: "Last 12 hours", hours: 12 },
  { value: "1d", label: "Last 1 day", hours: 24 },
] as const;

export default function AdminLLMStatus() {
  const { healthChecks, getLlmHealthChecks, isLoading, isFailed } =
    useGetLlmHealthChecks();

  const [selectedTimeRange, setSelectedTimeRange] = useState<string>("1h");

  // Helper function to get date range based on selection
  const getDateRange = useCallback((timeRangeValue: string) => {
    const selectedRange = TIME_RANGES.find(
      (range) => range.value === timeRangeValue,
    );
    if (!selectedRange) return { from: undefined, to: undefined };

    const to = new Date();
    const from = new Date(to.getTime() - selectedRange.hours * 60 * 60 * 1000);
    return { from, to };
  }, []);

  // Memoized refresh function
  const refreshData = useCallback(() => {
    const { from, to } = getDateRange(selectedTimeRange);
    getLlmHealthChecks(from, to);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTimeRange, getDateRange]);

  // Handle time range change
  const handleTimeRangeChange = useCallback((value: string) => {
    setSelectedTimeRange(value);
  }, []);

  // Initial load
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Auto-refresh every minute
  useEffect(() => {
    const interval = setInterval(refreshData, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshData]);

  if (isLoading) {
    return <CenterLoader />;
  }

  if (isFailed) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>LLM Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-32 text-red-500">
            Failed to load LLM health checks
          </div>
        </CardContent>
      </Card>
    );
  }

  const totalChecks = healthChecks?.length || 0;
  const currentTimeRangeLabel =
    TIME_RANGES.find((r) => r.value === selectedTimeRange)?.label ||
    "Last 1 hour";

  // Reusable header controls
  const headerControls = (
    <div className="flex items-center gap-3">
      <Select value={selectedTimeRange} onValueChange={handleTimeRangeChange}>
        <SelectTrigger className="w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TIME_RANGES.map((range) => (
            <SelectItem key={range.value} value={range.value}>
              {range.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        variant="outline"
        size="sm"
        onClick={refreshData}
        disabled={isLoading}
        className="flex items-center gap-2"
      >
        <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
        Refresh
      </Button>
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-start">
          <div>
            <CardTitle>LLM Status</CardTitle>
            <p className="text-sm text-gray-500">
              Latest health status for each LLM/Client combination
              {totalChecks > 0 ? " with historical summary" : ""}
            </p>
          </div>
          {headerControls}
        </div>
      </CardHeader>
      <CardContent>
        {totalChecks > 0 ? (
          <LlmHealthVisualization
            healthChecks={healthChecks || []}
            timeRangeLabel={currentTimeRangeLabel}
          />
        ) : (
          <div className="text-center text-gray-500 py-8">
            No health checks found in the {currentTimeRangeLabel.toLowerCase()}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
